<template>
  <div>
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <p class="title is-4">
            Appointment Times
          </p>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <div class="field" v-if="canEditSlots">
            <p class="control">
              <button
                type="button"
                class="button ml-4"
                @click="addDateForm.showing = !addDateForm.showing"
              >
                <span class="icon"><i class="fas fa-plus"></i></span>
                <span>Add Date</span>
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <p v-if="schedulingHasStarted">
      Scheduling has started. Dates and times are no longer editable.
    </p>
    <div class="columns" v-if="addDateForm.showing && canEditSlots">
      <div class="column is-4">
        <form @submit.prevent="addDate">
          <div class="field">
            <p class="control">
              <input
                type="date"
                class="input"
                v-model="addDateForm.slots_date"
                required
              />
            </p>
          </div>
          <div class="field">
            <p class="control">
              <button type="submit" class="button">Add Date</button>
              <button
                type="button"
                class="button is-text"
                @click="closeAddDateForm"
              >
                Cancel
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>

    <section class="my-8" v-for="meetingDate in slotKeys" :key="meetingDate">
      <h3 class="subtitle is-5">
        <span class="icon"><i class="far fa-calendar"></i></span>
        <span>{{ formatDate(meetingDate) }}</span>
      </h3>
      <div class="box">
        <table class="table is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>Start Time</th>
              <th>End Time</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(timeslot, index) in meeting.appointment_slots[
                meetingDate
              ]"
              :key="`${meetingDate}-${index}`"
            >
              <td>{{ timeslot.start_time }}</td>
              <td>{{ timeslot.end_time }}</td>
              <td>
                <p class="control has-text-right" v-if="canEditSlots">
                  <button
                    type="button"
                    class="button is-danger is-small is-outlined"
                    @click="confirmDeleteTimeSlot(meetingDate, timeslot)"
                  >
                    <i class="far fa-trash-alt"></i>
                  </button>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="level" v-if="canEditSlots">
          <div class="level-left">
            <div v-if="addSlotForm.showing === meetingDate">
              <form @submit.prevent="addTimeSlot(meetingDate)">
                <div class="field is-horizontal">
                  <div class="field-body">
                    <div class="field">
                      <p class="control">
                        <label for="startTime" class="date-label">
                          Start:
                        </label>
                        <input
                          type="time"
                          id="startTime"
                          input-classes="input is-small"
                          placeholder="Start"
                          v-model="addSlotForm.start_time"
                        />
                      </p>
                    </div>
                    <div class="field">
                      <p class="control">
                        <label for="endTime" class="date-label"> End: </label>
                        <input
                          type="time"
                          id="endTime"
                          input-classes="input is-small"
                          placeholder="End"
                          v-model="addSlotForm.end_time"
                        />
                      </p>
                    </div>
                    <div class="field is-grouped">
                      <p class="control">
                        <button
                          type="submit"
                          class="button is-small is-primary"
                        >
                          Add
                        </button>
                      </p>
                      <p class="control">
                        <button
                          type="button"
                          class="button is-small is-text"
                          @click="closeAddSlotForm"
                        >
                          Cancel
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <p v-else class="control">
              <button
                type="button"
                class="button is-small"
                @click="openAddSlotForm(meetingDate)"
              >
                <span class="icon"><i class="fas fa-plus"></i></span>
                <span>Add Time Slot</span>
              </button>
            </p>
          </div>
          <div class="level-right">
            <div class="field is-grouped">
              <p class="control">
                <button
                  type="button"
                  class="button is-text"
                  @click="confirmDeleteDate(meetingDate)"
                >
                  <span>Delete</span>
                </button>
              </p>
            </div>
          </div>
        </div>
        <div class="field" v-if="canEditSlots">
          <p class="control">
            <button
              type="button"
              class="button is-small"
              @click="openCopySlotForm(meetingDate)"
            >
              <span class="icon"><i class="fas fa-plus"></i></span>
              <span>Copy To New Date</span>
            </button>
          </p>
        </div>
      </div>
      <div class="modal" :class="{ 'is-active': copyDateForm.showing }">
        <div class="modal-background"></div>
        <div class="modal-card">
          <form @submit.prevent="closeCopySlotForm">
            <header class="modal-card-head">
              <p class="modal-card-title">Copying Timeslots To New Date</p>
              <button
                class="delete"
                aria-label="close"
                @click="closeCopySlotForm"
              ></button>
            </header>
            <section class="modal-card-body pd-2">
              <p class="control">
                Copying all {{ copyMeetingSlotLength }} slots from
                {{ formatDate(copyDateForm.sourceDate) }}
              </p>
              <div class="field">
                <label class="label">Enter New Date:</label>
                <p class="control">
                  <input
                    type="date"
                    required="required"
                    v-model="copyDateForm.targetDate"
                    class="input"
                  />
                </p>
              </div>
            </section>
            <footer class="modal-card-foot">
              <button class="button is-success" @click="copySlotToDate">
                Copy Schedule
              </button>
              <button class="button is-text" @click="closeCopySlotForm">
                Cancel
              </button>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import swal from 'sweetalert';
import { db, fixForAbc2023 } from '@/services/firebase';
import { formatDate, sortTimeSlots } from '@/services/utils';

export default {
  data() {
    return {
      meeting: {},
      addSlotForm: {
        showing: null,
        start_time: '',
        end_time: ''
      },
      addDateForm: {
        showing: false,
        slots_date: null
      },
      copyDateForm: {
        showing: false,
        targetDate: '',
        sourceDate: ''
      }
    };
  },

  firestore() {
    return {
      meeting: db.collection('meetings').doc(this.$route.params.id)
    };
  },

  computed: {
    appointmentId() {
      return this.$route.params.id;
    },

    canEditSlots() {
      return this.meeting.phase_index === 0 && !this.schedulingHasStarted;
    },

    slotKeys() {
      if (!this.meeting.appointment_slots) return [];
      return Object.keys(this.meeting.appointment_slots).sort();
    },

    copyMeetingSlotLength() {
      return this.meeting.appointment_slots[this.copyDateForm.sourceDate]
        ? this.meeting.appointment_slots[this.copyDateForm.sourceDate].length
        : '0';
    },
    schedulingHasStarted() {
      // if any suites have appointment times, this will be true and any
      // UI attached to changing meeting dates/times will be disabled.
      const meetingSuites = this.$store.getters.meetingSuites;
      if (meetingSuites) {
        if (
          Object.entries(meetingSuites).filter(([key, value]) => {
            return (
              !!value.appointment_slots &&
              !!Object.keys(value.appointment_slots).length
            );
          }).length
        ) {
          return true;
        }
      }
      return false;
    }
  },

  methods: {
    fixAbc2023() {
      swal({
        title: 'Are you sure?',
        text:
          'By proceeding, the meeting fix script will run. You do not want to run this more than once.',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, run script',
            value: true,
            visible: true,
            className: 'button is-danger is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          fixForAbc2023();
        }
      });
    },

    addDate() {
      let newDates = { ...this.meeting.appointment_slots };
      newDates[this.addDateForm.slots_date] = [];

      this.$firestoreRefs.meeting.update({
        appointment_slots: newDates
      });

      this.closeAddDateForm();
    },

    closeAddDateForm() {
      this.addDateForm.showing = false;
      this.addDateForm.slots_date = null;
    },

    confirmDeleteDate(meetingDate) {
      swal({
        title: 'Are you sure?',
        text:
          'By proceeding, this meeting date and all of its time slots will be removed.',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, remove date',
            value: true,
            visible: true,
            className: 'button is-danger is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          console.info('Date was deleted');
          this.deleteDate(meetingDate);
        }
      });
    },

    deleteDate(meetingDate) {
      let newAppointmentSlots = this.meeting.appointment_slots;
      delete newAppointmentSlots[meetingDate];

      this.$firestoreRefs.meeting.update({
        appointment_slots: newAppointmentSlots
      });
    },

    confirmDeleteTimeSlot(meetingDate, timeslot) {
      swal({
        title: 'Are you sure?',
        text: 'By proceeding, this time slot will be removed.',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            value: null,
            visible: true,
            className: 'button is-light',
            closeModal: true
          },
          confirm: {
            text: 'Yes, remove slot',
            value: true,
            visible: true,
            className: 'button is-danger is-inverted',
            closeModal: true
          }
        },
        dangerMode: true
      }).then(proceed => {
        if (proceed) {
          this.deleteTimeSlot(meetingDate, timeslot);
        }
      });
    },

    deleteTimeSlot(meetingDate, timeslot) {
      const newSlots = {};
      const remainingSlots = this.meeting.appointment_slots[meetingDate].filter(
        slot => {
          return !(
            slot.start_time === timeslot.start_time &&
            slot.end_time === timeslot.end_time
          );
        }
      );

      newSlots[`appointment_slots.${meetingDate}`] = remainingSlots;

      this.$firestoreRefs.meeting.update(newSlots);
    },

    openAddSlotForm(meetingDate) {
      this.addSlotForm.showing = meetingDate;
    },

    closeAddSlotForm() {
      this.addSlotForm.showing = null;
      this.addSlotForm.start_time = '';
      this.addSlotForm.end_time = '';
    },

    addTimeSlot(meetingDate) {
      // easier to just use destructuring
      // eslint-disable-next-line
      const { start_time, end_time } = this.addSlotForm;
      let newSlots = {};
      let appointmentSlots = this.meeting.appointment_slots[meetingDate]; // use deep clone?
      // check that the start time is earlier than the end time
      if (!isStartBeforeEnd(start_time, end_time)) {
        showTimeErrorModal();
        return;
      }

      const startTime = extractTimeString(start_time);
      const endTime = extractTimeString(end_time);
      const id = generateIDfromDate(startTime, endTime, meetingDate);

      appointmentSlots.push({
        id: id,
        start_time: startTime,
        end_time: endTime,
        is_open: true,
        scheduled_with: '',
        pending: []
      });

      newSlots[`appointment_slots.${meetingDate}`] = appointmentSlots.sort(
        sortTimeSlots
      );

      this.$firestoreRefs.meeting.update(newSlots);

      this.closeAddSlotForm();
    },

    // open copy modal and set date to copy to the chosen meeting date
    openCopySlotForm(meetingDate) {
      this.copyDateForm.sourceDate = meetingDate;
      this.copyDateForm.showing = true;
    },

    closeCopySlotForm() {
      this.copyDateForm.sourceDate = '';
      this.copyDateForm.targetDate = '';
      this.copyDateForm.showing = false;
    },

    // copies an existing day's schedule into a new day
    copySlotToDate() {
      // clone the schedule you want to copy, but replace the UIDs with ones generated using the new date
      const clonedMeeting = [
        ...this.meeting.appointment_slots[this.copyDateForm.sourceDate]
      ].map(slot => {
        return {
          ...slot,
          id: generateIDfromDate(
            slot.start_time,
            slot.end_time,
            this.copyDateForm.targetDate
          )
        };
      });

      // add new meeting date to an updated object
      const updatedAppointments = {
        ...this.meeting.appointment_slots,
        [this.copyDateForm.targetDate]: clonedMeeting
      };

      // update firebase with the meeting object containing the cloned meeting
      this.$firestoreRefs.meeting.update({
        appointment_slots: updatedAppointments
      });

      console.log(
        `copied the schedule of ${this.copyDateForm.sourceDate} into ${this.copyDateForm.targetDate}`
      );
    },

    formatDate
  }
};

function extractTimeString(timeString) {
  let hours = parseInt(timeString.substring(0, 2));
  const minutes = timeString.substring(3, 5);
  // time input uses military time so we parse that into AM or PM
  const AMorPM = hours < 12 ? 'am' : 'pm';
  // check for variable and string status to prevent error on lowercase
  // subtract 12 b/c of military time
  if (AMorPM && typeof AMorPM === 'string' && AMorPM.toLowerCase() === 'pm') {
    hours -= 12;
  }
  // 12 in the hours evaluates to zero for AM, and PM after the above transform
  // so we handle that here by adding 12
  hours = hours === 0 ? 12 : hours;
  // add a leading zero to single digit hours
  hours = hours.toString().length === 1 ? `0${hours}` : hours;
  return `${hours}:${minutes} ${AMorPM}`;
}

function isStartBeforeEnd(startTimeString, endTimeString) {
  const start = startTimeString.split(':').map(time => parseInt(time));
  const end = endTimeString.split(':').map(time => parseInt(time));

  // convert times into minutes since midnight for easier comparison
  const startTimeInMinutes = start[0] * 60 + start[1];
  const endTimeInMinutes = end[0] * 60 + end[1];

  if (startTimeInMinutes < endTimeInMinutes) {
    return true;
  }
  return false;
}

function showTimeErrorModal() {
  swal({
    title: 'Invalid Time Slot',
    text: 'Meeting start must be before meeting end.',
    icon: 'warning',
    buttons: {
      confirm: {
        text: 'OK.',
        value: true,
        visible: true,
        className: 'button is-danger is-inverted',
        closeModal: true
      }
    },
    dangerMode: true
  });
}

// base64 encode id from inputs, strip off last character which is equal sign
function generateIDfromDate(startTime, endTime, meetingDate) {
  return btoa(startTime.concat(endTime, meetingDate)).slice(0, -1);
}
</script>



// WEBPACK FOOTER //
// src/components/meeting/AppointmentTimes.vue